import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { PostCard } from "@/components/posts/PostCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/EmptyState";
import { Heart, Store, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CustomerFavorites() {
    const { toggleFavorite } = useAuth();
    const [tab, setTab] = useState("POST");
    const [postItems, setPostItems] = useState([]);
    const [shopItems, setShopItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [p, s] = await Promise.all([
                api.get("/me/favorites?target_type=POST&limit=200"),
                api.get("/me/favorites?target_type=SHOP&limit=200"),
            ]);
            setPostItems(p.data.items || []);
            setShopItems(s.data.items || []);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const unfavPost = async (postId) => {
        try {
            await toggleFavorite(postId);
            setPostItems((arr) => arr.filter((i) => i.post_id !== postId));
        } catch { toast.error("Could not unfavorite"); }
    };

    const unfavShop = async (shopId) => {
        try {
            await api.post("/me/favorites/toggle", { target_type: "SHOP", target_id: shopId });
            setShopItems((arr) => arr.filter((i) => i.shop_id !== shopId));
        } catch { toast.error("Could not unfavorite"); }
    };

    return (
        <div data-testid="customer-favorites">
            <h1 className="section-title text-2xl">My Favorites</h1>
            <p className="mt-1 text-sm text-[var(--text-muted)]">Posts and shops you&apos;ve saved.</p>

            <Tabs value={tab} onValueChange={setTab} className="mt-4">
                <TabsList data-testid="favs-tabs">
                    <TabsTrigger value="POST" data-testid="favs-tab-posts">
                        <Heart size={14} className="mr-2" /> Posts ({postItems.length})
                    </TabsTrigger>
                    <TabsTrigger value="SHOP" data-testid="favs-tab-shops">
                        <Store size={14} className="mr-2" /> Shops ({shopItems.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="POST" className="mt-4">
                    {loading ? (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}
                        </div>
                    ) : postItems.length === 0 ? (
                        <EmptyState
                            title="No favorite posts yet"
                            hint="Tap the heart on any post to save it here."
                            icon={Heart}
                            action={<Link to="/posts"><button type="button" className="btn-primary">Explore posts</button></Link>}
                        />
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="favs-posts-grid">
                            {postItems.map((it) => (
                                <div key={it.post_id} className="relative">
                                    <PostCard post={{
                                        id: it.post_id, title: it.title, type: it.type,
                                        poster_image_url: it.poster_image_url, category_slug: it.category_slug,
                                        district_slug: it.district_slug, business_name: it.business_name,
                                        details: { discount_percent: it.discount_percent, price_after: it.price_after },
                                        valid_to: it.valid_to, favorite_count: it.favorite_count, status: "PUBLISHED",
                                    }} />
                                    <button onClick={() => unfavPost(it.post_id)} data-testid={`fav-remove-${it.post_id}`}
                                        className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-neutral-700 shadow hover:bg-rose-50 hover:text-rose-600">
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="SHOP" className="mt-4">
                    {loading ? (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
                        </div>
                    ) : shopItems.length === 0 ? (
                        <EmptyState title="No favorite shops yet" hint="Tap the heart on a shop card to follow their offers." icon={Store} />
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="favs-shops-grid">
                            {shopItems.map((it) => (
                                <div key={it.shop_id} className="card-soft flex items-center gap-3 !p-3" data-testid={`fav-shop-${it.shop_id}`}>
                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100 font-heading text-lg font-bold text-[var(--brand-orange)]">
                                        {(it.business_name || "?").charAt(0).toUpperCase()}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-heading font-semibold text-[var(--text-primary)]">{it.business_name}</div>
                                        <div className="text-[11px] text-[var(--text-muted)] capitalize">{it.business_district} · {(it.business_category || "").replace(/_/g, " ")}</div>
                                    </div>
                                    <Link to={`/posts?district_slug=${it.business_district}`} className="section-link">View</Link>
                                    <Button size="icon" variant="ghost" onClick={() => unfavShop(it.shop_id)} className="h-8 w-8"><X size={14} /></Button>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
